<template>
  <div id="login-screen">
    <img src="./assets/logo.png" alt="Logo" class="centered-logo">
    <div class="logo-button-spacer"></div>
    <div class="signin-container">
      <div id="g_id_onload" data-auto_prompt="false"></div>
      <div class="g_id_signin" data-type="standard"></div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { useToast } from 'vue-toast-notification';
import { useRouter } from 'vue-router';
import './LoginScreen.vue.css';
import { GameStreamingService } from './GameManager';
import { useAuthStore } from './stores/auth';

// Define types for Google Sign-In
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
          }) => void;
          renderButton: (element: HTMLElement, options: {
            theme: string;
            size: string;
          }) => void;
        };
      };
    };
  }
}

// Interface defining the structure of component data
interface ComponentData {
  clientId: string;
  showGoogleSignIn : boolean;
}

// Add interface for the event detail
interface NavigationEventDetail {
    route: string;
}

// Define the event type
type NavigationEvent = CustomEvent<NavigationEventDetail>;

export default defineComponent({
  emits: ['auth-success'],
  setup() {
    const router = useRouter();
    const toast = useToast();
    const auth = useAuthStore();
    return { router, toast, auth };
  },
  data(): ComponentData {
    return {
      clientId: '532849151962-gerup4k4k85s5b5n1rq7rnbq573fbgh9.apps.googleusercontent.com',
      showGoogleSignIn: true
    };
  },
  mounted() {
    // Add event listener for token expiration
    window.addEventListener('tokenExpired', this.handleTokenExpiration);
    
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');

    if (token) {
      // Remove token from URL without page reload
      const newUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, '', newUrl);

      this.showGoogleSignIn = false; // Hide Google sign-in initially
      this.handleCredentialResponse({credential: token});
    } else {
      this.loadGoogleSignInScript();
    }
  },
  beforeUnmount() {
    window.removeEventListener('tokenExpired', this.handleTokenExpiration);
  },
  methods: {
    loadGoogleSignInScript() {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        this.initializeGoogleSignIn();
      };
      document.head.appendChild(script);
    },
    initializeGoogleSignIn() {
      if (window.google) {
        window.google.accounts.id.initialize({
          client_id: this.clientId,
          callback: this.handleCredentialResponse,
        });
        window.google.accounts.id.renderButton(
          document.querySelector('.g_id_signin') as HTMLElement,
          { theme: 'outline', size: 'large' }
        );
      }
    },
    async loadConfig(): Promise<string> {
        try {
            const response = await fetch('/devconfig.json'); // Adjust path as necessary
            const config = await response.json();
            if (config.developmentIP == "") {
                return `/api`;
            } else {
                return `http://${config.developmentIP}:3003/api`;
            }
        } catch (error) {
            console.error('Failed to load config:', error);
            return `/api`;
        }
    },
    async handleCredentialResponse(response: any) {
        try {
            const gameService = GameStreamingService.getInstance();
            console.log('GameService available:', !!gameService);
            
            const token = response.credential;
            console.log(`Setting token (length: ${token.length})`);
            gameService.setToken(token);
            
            console.log('Starting connection...');
            console.log('Calling connect()...');
            try {
                console.log('About to await connect()');
                const connectPromise = gameService.connect();
                console.log('Connect promise created, awaiting...');
                await connectPromise;
                console.log('Connect completed successfully');
            } catch (error) {
                console.error('Connect failed in try/catch:', error);
                throw error;
            }
            
            console.log('Connection successful, parsing JWT...');
            try {
                const payload = JSON.parse(atob(token.split('.')[1]));
                console.log('JWT parsed successfully:', { 
                    email: payload.email, 
                    name: payload.name 
                });
                this.auth.setAuth(
                    token,
                    {
                        name: payload.name,
                        email: payload.email
                    },
                    'devicepros.net'
                );
                
                // Wait for audio initialization
                await new Promise<void>((resolve) => {
                    const handler = (e: Event) => {
                        const event = e as CustomEvent<NavigationEventDetail>;
                        window.removeEventListener('readyForNavigation', handler);
                        resolve();
                        
                        // Add error checking
                        if (!event.detail?.route) {
                            console.error('Navigation event missing route:', event);
                            return;
                        }
                        
                        // Navigate to the route specified in the event
                        console.log('Navigating to route:', event.detail.route);
                        this.router.push(event.detail.route);
                    };
                    window.addEventListener('readyForNavigation', handler);
                });
                
                console.log('Auth success stored');
            } catch (error) {
                console.error('Failed to parse JWT:', error);
                throw error;
            }
            
        } catch (error: any) {
            console.error('Authentication failed:', error);
            this.toast.error(error.message || 'Authentication failed. Please try again.');
        }
    },
    handleTokenExpiration() {
        this.auth.clearAuth();
        this.showGoogleSignIn = true;
        
        // Re-initialize Google Sign-In
        this.loadGoogleSignInScript();
        
        // Optional: Show a more prominent notification
        this.toast.error('Your session has expired. Please log in again.', {
            position: 'top',
            duration: 5000,
        });
    }
  }
});
</script>
